import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PromptInputBox } from '@/components/ui/ai-prompt-box';
import { BrainCog, Sparkles, CheckCircle } from 'lucide-react';

const MOCK_RESPONSES = [
  "Merci pour votre message ! Notre équipe vous contactera sous 24h. En attendant, n'hésitez pas à consulter notre portfolio.",
  "Message bien reçu ! Nous analysons votre demande et reviendrons vers vous très rapidement avec une proposition adaptée.",
  "Super projet ! Nos experts vont étudier votre demande et vous enverront une réponse personnalisée dans les meilleurs délais.",
];

export const AiContact = () => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleSend = (message, files) => {
    if (!message.trim() && (!files || files.length === 0)) return;

    // Add user message
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: message, timestamp: new Date() },
    ]);

    setIsLoading(true);

    // Simulate AI response delay
    setTimeout(() => {
      const response = MOCK_RESPONSES[Math.floor(Math.random() * MOCK_RESPONSES.length)];
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: response, timestamp: new Date() },
      ]);
      setIsLoading(false);
    }, 1400);
  };

  const formatTime = (date) =>
    date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  return (
    <section
      id="ai-contact"
      data-testid="ai-contact-section"
      className="relative py-24 sm:py-32"
    >
      {/* Ambient glow */}
      <div
        className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[300px] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse, rgba(168,85,247,0.07) 0%, transparent 65%)',
          filter: 'blur(60px)',
        }}
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#a855f7]/10 border border-[#a855f7]/25 mb-6">
            <BrainCog className="w-4 h-4 text-[#a855f7]" />
            <span className="text-white/75 text-sm font-medium">Assistant IA</span>
          </div>

          <h2 className="font-outfit text-4xl sm:text-5xl font-bold tracking-tight text-white mb-5">
            Parlez à notre{' '}
            <span className="text-gradient-premium">assistant</span>
          </h2>
          <p className="text-white/60 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
            Décrivez votre projet directement. Notre IA analyse votre demande et notre équipe vous répond sous 24h.
          </p>
          <div className="section-glow-divider max-w-xs mx-auto mt-8" />
        </motion.div>

        {/* Chat area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-4"
        >
          {/* Message thread */}
          <AnimatePresence>
            {messages.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="space-y-3 mb-4 max-h-80 overflow-y-auto pr-1"
                style={{ scrollbarWidth: 'thin' }}
              >
                {messages.map((msg, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} gap-3`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#4facfe] to-[#a855f7] flex items-center justify-center flex-shrink-0 mt-0.5 shadow-[0_0_12px_rgba(168,85,247,0.3)]">
                        <Sparkles className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-gradient-to-r from-[#00f2fe]/20 to-[#4facfe]/20 border border-[#4facfe]/25 text-white ml-auto rounded-tr-sm'
                          : 'bg-white/[0.05] border border-white/[0.08] text-white/85 rounded-tl-sm'
                      }`}
                    >
                      {msg.content}
                      <div className="text-[10px] text-white/35 mt-1.5">
                        {formatTime(msg.timestamp)}
                      </div>
                    </div>
                  </motion.div>
                ))}

                {/* Loading indicator */}
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-start gap-3"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#4facfe] to-[#a855f7] flex items-center justify-center flex-shrink-0 shadow-[0_0_12px_rgba(168,85,247,0.3)]">
                      <Sparkles className="w-4 h-4 text-white animate-pulse" />
                    </div>
                    <div className="bg-white/[0.05] border border-white/[0.08] rounded-2xl rounded-tl-sm px-4 py-3">
                      <div className="flex gap-1 items-center h-4">
                        {[0, 1, 2].map((i) => (
                          <div
                            key={i}
                            className="w-1.5 h-1.5 rounded-full bg-[#4facfe]/70 animate-bounce"
                            style={{ animationDelay: `${i * 0.15}s` }}
                          />
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Empty state hint */}
          {messages.length === 0 && (
            <div className="flex flex-wrap gap-2 mb-4 justify-center">
              {[
                '🚀 Je veux créer une app mobile',
                '💼 J\'ai besoin d\'un SaaS',
                '🎨 Refonte de mon site web',
              ].map((hint) => (
                <button
                  key={hint}
                  onClick={() => handleSend(hint, [])}
                  className="px-4 py-2 rounded-full text-sm bg-white/[0.04] border border-white/[0.08] text-white/65 hover:text-white hover:border-[#4facfe]/30 hover:bg-[#4facfe]/[0.06] transition-all duration-200"
                >
                  {hint}
                </button>
              ))}
            </div>
          )}

          {/* AI Prompt Box */}
          <PromptInputBox
            onSend={handleSend}
            isLoading={isLoading}
            placeholder="Décrivez votre projet... (ex: application mobile e-commerce)"
            className="border-[#4facfe]/15 bg-[#060c1e]/80 backdrop-blur-xl shadow-[0_8px_40px_rgba(0,0,0,0.5),0_0_0_1px_rgba(79,172,254,0.08)]"
          />

          {/* Trust signal */}
          {messages.some((m) => m.role === 'assistant') && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center text-white/40 text-xs flex items-center justify-center gap-1.5 mt-3"
            >
              <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
              Message transmis — réponse humaine sous 24h
            </motion.p>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default AiContact;
